/**
 * meleeSwingShape — pure geometry for melee swing visuals (lane AZ, plan 23
 * row 50).
 *
 * DJ's note: "Melee abilities need more feel too, like Lightning Touch" — the
 * swing was a 4 px red line drawn at the attack range around the hitbox's
 * TOP-LEFT corner, then scaled 1.5× outward, so it floated far from the body
 * with nothing joining it to the attacker. These helpers describe a swing
 * that is anchored on the body centre and ends exactly at the cone's reach:
 *
 *  - `crescentPoints` — a filled smear whose outer edge is the reach arc and
 *    whose thickness tapers to points at both ends (thickest mid-swing), so
 *    the shape reads as a blade trail leaving the body.
 *  - `wedgePoints` — the faint swept area from the body edge to the reach.
 *  - `boltPoints` — a jagged polyline between two points (Lightning Touch).
 *  - `sparkRays` — short radial segments for a contact spark burst.
 *
 * Everything is computed ONCE per swing, around the local origin; the
 * renderer animates the result by transform/alpha only (Pixi 8: a Graphics
 * redraw rebuilds the stage — docs/guides/PERF_ANALYSIS.md).
 */

export interface Point { x: number; y: number }

const DEG = Math.PI / 180;

export interface CrescentSpec {
  /** Outer radius (px) — the swing's real reach. */
  reach: number;
  /** Start angle in degrees for a RIGHT-facing attacker (0 = ahead, negative = up). */
  arcAngleDeg: number;
  /** Total sweep in degrees. */
  arcSpreadDeg: number;
  /** Mid-swing thickness as a fraction of reach (0..1). */
  thicknessFraction: number;
  /** Mirror across the vertical axis (left-facing attacker). */
  facingLeft: boolean;
  /** Samples along the arc (>= 2). */
  segments?: number;
}

/** Angle (radians) of a right-facing angle mirrored for the facing. */
function facedAngle(deg: number, facingLeft: boolean): number {
  const a = deg * DEG;
  return facingLeft ? Math.PI - a : a;
}

/**
 * Closed polygon: the outer reach arc start → end, then the tapered inner
 * edge end → start. Both ends meet at the outer radius (zero thickness).
 */
export function crescentPoints(spec: CrescentSpec): Point[] {
  const n = Math.max(2, Math.floor(spec.segments ?? 14));
  const thickness = Math.max(0, Math.min(1, spec.thicknessFraction)) * spec.reach;
  const outer: Point[] = [];
  const inner: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const a = facedAngle(spec.arcAngleDeg + spec.arcSpreadDeg * u, spec.facingLeft);
    const r = spec.reach;
    const rIn = r - thickness * Math.sin(Math.PI * u);
    outer.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    inner.push({ x: Math.cos(a) * rIn, y: Math.sin(a) * rIn });
  }
  return [...outer, ...inner.reverse()];
}

/**
 * The whole swept wedge between `innerRadius` (the body's edge) and the
 * reach — a faint fill under the crescent so the swing visibly starts AT the
 * body instead of floating out at the reach.
 */
export function wedgePoints(spec: Omit<CrescentSpec, 'thicknessFraction'>, innerRadius: number): Point[] {
  const n = Math.max(2, Math.floor(spec.segments ?? 14));
  const outer: Point[] = [];
  const inner: Point[] = [];
  const rIn = Math.max(0, Math.min(innerRadius, spec.reach));
  for (let i = 0; i <= n; i++) {
    const a = facedAngle(spec.arcAngleDeg + spec.arcSpreadDeg * (i / n), spec.facingLeft);
    outer.push({ x: Math.cos(a) * spec.reach, y: Math.sin(a) * spec.reach });
    inner.push({ x: Math.cos(a) * rIn, y: Math.sin(a) * rIn });
  }
  return [...outer, ...inner.reverse()];
}

/** The reach arc's end angles (radians), already mirrored for the facing. */
export function crescentArcAngles(spec: Pick<CrescentSpec, 'arcAngleDeg' | 'arcSpreadDeg' | 'facingLeft'>): { start: number; end: number } {
  const a = facedAngle(spec.arcAngleDeg, spec.facingLeft);
  const b = facedAngle(spec.arcAngleDeg + spec.arcSpreadDeg, spec.facingLeft);
  return spec.facingLeft ? { start: b, end: a } : { start: a, end: b };
}

/**
 * Lane BB: the radius the smear is DRAWN at — `scale` of the real reach, at
 * least `minPx`, never past the reach itself. Visual only; the hit cone keeps
 * the full reach.
 */
export function smearDrawRadius(reach: number, scale: number, minPx: number): number {
  const r = Math.max(0, reach);
  return Math.min(r, Math.max(minPx, r * scale));
}

/** Deterministic PRNG (mulberry32) so a seeded bolt is reproducible in tests. */
export function seededRandom(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Jagged bolt from `a` to `b`: endpoints exact, interior points displaced
 * perpendicular to the segment by up to `jitter` px, tapering to zero at both
 * ends so the bolt visibly leaves the hand and lands on the target.
 */
export function boltPoints(a: Point, b: Point, segments: number, jitter: number, rand: () => number): Point[] {
  const n = Math.max(2, Math.floor(segments));
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const pts: Point[] = [{ x: a.x, y: a.y }];
  for (let i = 1; i < n; i++) {
    const u = i / n;
    const off = (rand() * 2 - 1) * jitter * Math.sin(Math.PI * u);
    pts.push({ x: a.x + dx * u + nx * off, y: a.y + dy * u + ny * off });
  }
  pts.push({ x: b.x, y: b.y });
  return pts;
}

/** `count` radial segments around the origin, from `inner` to `outer` px. */
export function sparkRays(count: number, inner: number, outer: number, rand: () => number): Array<[Point, Point]> {
  const rays: Array<[Point, Point]> = [];
  const n = Math.max(1, Math.floor(count));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + (rand() - 0.5) * (Math.PI / n);
    const len = outer * (0.7 + rand() * 0.3);
    rays.push([
      { x: Math.cos(a) * inner, y: Math.sin(a) * inner },
      { x: Math.cos(a) * len, y: Math.sin(a) * len },
    ]);
  }
  return rays;
}

/** Ease-out cubic, 0..1 → 0..1. */
export function easeOutCubic(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return 1 - (1 - c) ** 3;
}

/** Mix a 0xRRGGBB colour toward white by `amount` (0..1). */
export function lighten(color: number, amount: number): number {
  const k = Math.min(1, Math.max(0, amount));
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const mix = (c: number) => Math.round(c + (255 - c) * k);
  return (mix(r) << 16) | (mix(g) << 8) | mix(b);
}
